import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ExternalLink from '../ExternalLink/ExternalLink';
import { InlineTextButton } from '../Button/Button';
import Icons from '../Icons/Icons';
import { isWindowDefined } from '../../util/cartHelpers';

import css from './Banner.module.css';

const readStoredDismissed = storageKey => {
  if (!storageKey || !isWindowDefined() || !window.localStorage) {
    return false;
  }

  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch (error) {
    return false;
  }
};

const storeDismissed = storageKey => {
  if (!storageKey || !isWindowDefined() || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, 'true');
  } catch (error) {
    // Ignore write errors (e.g. storage disabled).
  }
};

const Banner = props => {
  const {
    rootClassName = null,
    className = null,
    icon = null,
    message = null,
    ctaLabel = null,
    ctaHref = null,
    ctaOnClick = null,
    dismissible = true,
    dismissLabel = 'Dismiss banner',
    onDismiss = null,
    storageKey = null,
    children = null,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const [dismissed, setDismissed] = useState(() => readStoredDismissed(storageKey));

  useEffect(() => {
    setDismissed(readStoredDismissed(storageKey));
  }, [storageKey]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    storeDismissed(storageKey);
    if (onDismiss) {
      onDismiss();
    }
  };

  const content = message || children;
  const shouldRenderCta = !!ctaLabel && (ctaHref || ctaOnClick);
  const cta = shouldRenderCta ? (
    ctaHref ? (
      <ExternalLink className={css.ctaLink} href={ctaHref}>
        {ctaLabel}
      </ExternalLink>
    ) : (
      <InlineTextButton rootClassName={css.ctaButton} onClick={ctaOnClick} type="button">
        {ctaLabel}
      </InlineTextButton>
    )
  ) : null;

  return (
    <div className={classes} role="status" aria-live="polite">
      <div className={css.content}>
        {icon ? (
          <span className={css.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <p className={css.message}>{content}</p>
      </div>
      <div className={css.actions}>
        {cta}
        {dismissible ? (
          <button
            type="button"
            className={css.dismissButton}
            onClick={handleDismiss}
            aria-label={dismissLabel}
          >
            <Icons name="close" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

const { bool, func, node, string } = PropTypes;

Banner.propTypes = {
  rootClassName: string,
  className: string,
  icon: node,
  message: node,
  ctaLabel: string,
  ctaHref: string,
  ctaOnClick: func,
  dismissible: bool,
  dismissLabel: string,
  onDismiss: func,
  storageKey: string,
  children: node,
};

export default Banner;
